import { Link } from "react-router-dom";
import { ArrowRight, Phone, ClipboardList, Car, MapPin, Mail } from "lucide-react";
import { FadeUp, MaskLines, SectionLabel } from "./Reveal";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const ICONS = [ClipboardList, Car, MapPin, Mail];

export const QuoteCta = () => {
  const { t } = useLang();
  const q = t.quote;
  return (
    <section id="quote" data-testid="quote-section" className="relative overflow-hidden bg-ink py-24 md:py-36">
      <div className="relative mx-auto grid max-w-[1440px] gap-14 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionLabel n="06">{q.label}</SectionLabel>
          <h2 className="font-display mt-8 text-4xl font-black uppercase leading-[0.92] text-bone sm:text-5xl lg:text-6xl">
            <MaskLines inView lines={[q.l1, q.l2, <span key="f" className="font-serif-i normal-case text-brand">{q.l3}</span>]} />
          </h2>
          <FadeUp>
            <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-300 md:text-lg">{q.body}</p>
            <a data-testid="quote-call-link" href={PHONE_TEL} className="group mt-10 inline-flex items-center gap-4">
              <span className="grid h-14 w-14 place-items-center border border-brand/50 text-brand transition-[background-color,color] duration-300 group-hover:bg-brand group-hover:text-cream"><Phone className="h-5 w-5" /></span>
              <span>
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{q.hotline}</span>
                <span className="font-display block text-2xl font-extrabold text-bone md:text-3xl">{PHONE_DISPLAY}</span>
              </span>
            </a>
          </FadeUp>
        </div>
        <FadeUp className="lg:col-span-7">
          <div className="chamfer border border-white/15 bg-coal p-6 md:p-10">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand2">{q.ctaKicker}</div>
            <h3 className="font-display mt-3 text-2xl font-black uppercase md:text-3xl">{q.ctaTitle}</h3>
            <ol className="mt-8 grid gap-3 sm:grid-cols-2">
              {q.ctaSteps.map(([title, desc], i) => {
                const Icon = ICONS[i];
                return (
                  <li key={title} className="flex gap-4 border border-white/10 bg-ink p-4">
                    <span className="grid h-10 w-10 shrink-0 place-items-center bg-brand font-display text-sm font-black text-cream">{i + 1}</span>
                    <span>
                      <span className="flex items-center gap-2 font-display text-sm font-extrabold uppercase"><Icon className="h-4 w-4 text-brand" />{title}</span>
                      <span className="mt-1 block text-sm text-zinc-300">{desc}</span>
                    </span>
                  </li>
                );
              })}
            </ol>
            <Link to="/quote" data-testid="quote-start-button" className="group mt-8 inline-flex w-full items-center justify-center gap-3 bg-brand px-8 py-5 text-cream transition-transform duration-300 hover:-translate-y-0.5 sm:w-auto">
              <span className="font-display text-base font-extrabold uppercase tracking-wider">{q.submit}</span>
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">{q.free}</p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
